import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faMapMarkerAlt,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram, faDiscord, faTwitter } from '@fortawesome/free-brands-svg-icons';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Formspree endpoint for contact form
  const FORMSPREE_URL = 'https://formspree.io/f/xeorjnpg';

  // Alternative: Use a mailto link (simpler but opens email client)
  // const MAILTO_URL = 'mailto:contact@chainreach.io';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _subject: `New ChainReach Contact: ${formData.name}`,
        }),
      });

      if (response.ok) {
        setFormStatus('success');
        setStatusMessage('Thank you for your message! We\'ll get back to you within 24 hours.');
        setFormData({ name: '', email: '', project: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setFormStatus('error');
      setStatusMessage('Oops! Something went wrong. Please try again or email us directly.');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-20 relative">
      <div className="max-container section-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ type: 'spring' }}
            className="inline-block glass px-4 py-2 rounded-full mb-4"
          >
            <span className="text-sm text-verdigris">Contact Us</span>
          </motion.div>
          <h2 className="heading-2 text-linen mb-4">
            Let's Build Your <span className="gradient-text">Web3 Success</span>
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            Ready to take your blockchain project to the moon? Get in touch with our
            expert team and let's discuss your marketing strategy.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-8"
          >
            <h3 className="text-2xl font-semibold text-linen mb-6">Send us a Message</h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-linen-300 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-raisin-800 border border-linen/20 rounded-lg focus:border-verdigris focus:outline-none transition-colors text-linen placeholder-linen-500"
                    placeholder="John Doe"
                    required
                    disabled={formStatus === 'sending'}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-linen-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-raisin-800 border border-linen/20 rounded-lg focus:border-verdigris focus:outline-none transition-colors text-linen placeholder-linen-500"
                    placeholder="john@example.com"
                    required
                    disabled={formStatus === 'sending'}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="project" className="block text-linen-300 mb-2">
                  Project Type
                </label>
                <select
                  id="project"
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-raisin-800 border border-linen/20 rounded-lg focus:border-verdigris focus:outline-none transition-colors text-linen"
                  required
                  disabled={formStatus === 'sending'}
                >
                  <option value="">Select project type</option>
                  <option value="token">Token Launch</option>
                  <option value="nft">NFT Collection</option>
                  <option value="defi">DeFi Protocol</option>
                  <option value="gaming">GameFi/P2E</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-linen-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-raisin-800 border border-linen/20 rounded-lg focus:border-verdigris focus:outline-none transition-colors text-linen placeholder-linen-500 resize-none"
                  placeholder="Tell us about your project..."
                  required
                  disabled={formStatus === 'sending'}
                />
              </div>

              {/* Status Messages */}
              {formStatus !== 'idle' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg flex items-center space-x-2 ${
                    formStatus === 'success'
                      ? 'bg-verdigris/20 text-verdigris'
                      : formStatus === 'error'
                      ? 'bg-persian/20 text-persian'
                      : 'bg-vanilla/20 text-vanilla'
                  }`}
                >
                  <FontAwesomeIcon
                    icon={
                      formStatus === 'success'
                        ? faCheckCircle
                        : formStatus === 'error'
                        ? faExclamationCircle
                        : faPaperPlane
                    }
                    className="text-lg"
                  />
                  <span>
                    {formStatus === 'sending'
                      ? 'Sending your message...'
                      : statusMessage}
                  </span>
                </motion.div>
              )}

              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary w-full flex items-center justify-center space-x-2"
                disabled={formStatus === 'sending'}
              >
                <span>{formStatus === 'sending' ? 'Sending...' : 'Send Message'}</span>
                <FontAwesomeIcon icon={faPaperPlane} />
              </motion.button>
            </form>

            {/* Alternative Email Option */}
            <div className="mt-6 pt-6 border-t border-linen/10">
              <p className="text-linen-400 text-sm text-center">
                Prefer email? Reach us directly at{' '}
                <a
                  href="mailto:contact@chainreach.io"
                  className="text-verdigris hover:text-verdigris-400 transition-colors"
                >
                  contact@chainreach.io
                </a>
              </p>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Quick Contact */}
            <div className="glass rounded-xl p-8">
              <h3 className="text-2xl font-semibold text-linen mb-6">Get in Touch</h3>

              <div className="space-y-4">
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-persian to-verdigris rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faEnvelope} className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-linen-400 text-sm">Email</p>
                    <p className="text-linen">contact@chainreach.io</p>
                  </div>
                </motion.div>


                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-4"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-persian to-verdigris rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-linen-400 text-sm">Office</p>
                    <p className="text-linen">Global (Remote First)</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Social Links */}
            <div className="glass rounded-xl p-8">
              <h3 className="text-2xl font-semibold text-linen mb-6">Join Our Community</h3>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: faTelegram, label: 'Telegram', color: 'hover:bg-blue-500/20' },
                  { icon: faDiscord, label: 'Discord', color: 'hover:bg-indigo-500/20' },
                  { icon: faTwitter, label: 'Twitter', color: 'hover:bg-sky-500/20' },
                ].map((social) => (
                  <motion.a
                    key={social.label}
                    href="#"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`glass rounded-lg p-4 text-center ${social.color} transition-all`}
                  >
                    <FontAwesomeIcon icon={social.icon} className="text-2xl text-verdigris mb-2" />
                    <p className="text-linen-300 text-sm">{social.label}</p>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Response Time */}
            <div className="glass rounded-xl p-6 text-center">
              <p className="text-linen-400 mb-2">Average Response Time</p>
              <p className="text-3xl font-bold gradient-text">Under 2 Hours</p>
              <p className="text-linen-300 text-sm mt-2">
                We're here to help you succeed, 24/7
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;